import React, { useState, useEffect } from "react";
import { Box, VStack, Icon, Image, CloseButton, Text } from "@chakra-ui/react";
import { useDropzone } from "react-dropzone";
import { MdCloudUpload } from "react-icons/md";

interface StrongImageDragDropProps {
  onFileSelect: (file: File | null) => void;
  defaultImageUrl?: string;
  height?: string;
}

const StrongImageDragDrop: React.FC<StrongImageDragDropProps> = ({
  onFileSelect,
  defaultImageUrl,
  height = "60%", 
}) => {
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    defaultImageUrl || null,
  );

  useEffect(() => {
    setPreviewUrl(defaultImageUrl || null);
  }, [defaultImageUrl]);

  const onDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setThumbnail(file);
      setPreviewUrl(URL.createObjectURL(file));
      onFileSelect(file);
    }
  };

  const removeImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setThumbnail(null);
    setPreviewUrl(null);
    onFileSelect(null);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    maxFiles: 1,
  });

  return (
    <Box
      {...getRootProps()}
      p={2}
      border="2px dashed"
      borderColor={isDragActive ? "primary.500" : "gray.300"}
      borderRadius="md"
      textAlign="center"
      bg={isDragActive ? "primary.50" : "gray.50"}
      cursor="pointer"
    >
      <input {...getInputProps()} />
      {previewUrl ? (
        <Box position="relative" w="100%" maxW="500px" mx="auto">
          <Box
            position="relative"
            w="100%"
            pt={height}  // 3 / 5 = 60%, so this creates a 5:3 ratio box
            borderRadius="md"
            overflow="hidden"
            mb={2}
            bg="gray.100"
          >
            <Image
              src={previewUrl}
              alt="Uploaded thumbnail"
              position="absolute"
              top="0"
              left="0"
              w="100%"
              h="100%"
              objectFit="cover"
            />
          </Box>

          <CloseButton
            position="absolute"
            top="8px"
            right="8px"
            size="sm"
            bg="whiteAlpha.800"
            onClick={removeImage}
          />
        </Box>
      ) : (
        <VStack spacing={2}>
          <Icon as={MdCloudUpload} boxSize={8} color="primary.400" />
          <Text color="gray.600">
            {isDragActive
              ? "Drop the image here"
              : "Drag & drop an image here, or click to select"}
          </Text>
          <Text fontSize="sm" color="gray.500">
            Only image files are accepted
          </Text>
        </VStack>
      )}
    </Box>
  );
};

export default StrongImageDragDrop;
